import React, { useCallback, useEffect, useMemo } from 'react';
import { useField } from 'react-final-form';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { CheckboxField } from 'ksnm-common-ui/lib/Form/CheckboxField';
import { DateField } from 'ksnm-common-ui/lib/Form/DateField';
import { TextField } from 'ksnm-common-ui/lib/Form/InputFields';
import { RadioField } from 'ksnm-common-ui/lib/Form/RadioField';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { TextAreaField } from 'ksnm-common-ui/lib/Form/TextAreaField';
import { GroupPanel } from 'ksnm-common-ui/lib/GroupPanel/GroupPanel';
import { createMask } from 'ksnm-common-ui/lib/MaskedInput';

import { counterpartyEditStore } from '@stores/dictionaryStores/counterpartiesStore/counterpartyEditStore';
import { counterpartyTypesStore } from '@stores/dictionaryStores/counterpartyTypesStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';

import styles from './CounterpartyForm.module.scss';

const RESIDENT_OPTIONS = [
  { label: 'Резидент', value: true },
  { label: 'Нерезидент', value: false },
];

const COUNTERPARTY_OPTIONS = [
  { label: 'Юридическое лицо', value: true },
  { label: 'Физическое лицо', value: false },
];

const COMPANY_COUNTERPARTY_OPTIONS = [
  { label: 'Да', value: true },
  { label: 'Нет', value: false },
];

export const RUSSIAN_CODE = '643';

export const CounterpartyForm = observer(() => {
  const { isEditMode, isNew } = counterpartyEditStore;
  const { organizations } = organizationsStore;
  const { counterpartyTypes } = counterpartyTypesStore;

  useEffect(() => fetchReferences(['counterpartyTypes', 'organizations']), []);

  const {
    input: { value: resident, onChange: changeResident },
  } = useField('resident');

  const {
    input: { value: counterparty, onChange: changeCounterparty },
  } = useField('counterparty');

  const {
    input: { onChange: changeThesOksmCode },
  } = useField('thesOksmCode');

  const {
    input: { onChange: changeOrgLocal },
  } = useField('orgLocal');

  const {
    input: { value: companyCounterparty, onChange: changeCompanyCounterparty },
  } = useField('companyCounterparty');

  const {
    input: { onChange: changeKpp },
  } = useField('kpp');

  const {
    input: { onChange: changeOgrnip },
  } = useField('ogrnip');

  const {
    input: { onChange: changeCounterpartyLastName },
  } = useField('counterpartyLastName');

  const {
    input: { onChange: changeCounterpartyFirstName },
  } = useField('counterpartyFirstName');

  const {
    input: { onChange: changeCounterpartyMiddleName },
  } = useField('counterpartyMiddleName');

  const {
    input: { onChange: changeRegistrationNumber },
  } = useField('registrationNumber');

  const {
    input: { onChange: changeIsPayAgent },
  } = useField('isPayAgent');

  const onChangeResident = useCallback(
    (value) => {
      changeRegistrationNumber(undefined);
      changeKpp(undefined);
      changeResident(value.target.value);

      if (value.target.value) {
        changeThesOksmCode(RUSSIAN_CODE);
      } else {
        changeThesOksmCode(undefined);
      }
    },
    [changeKpp, changeRegistrationNumber, changeResident, changeThesOksmCode],
  );

  const onChangeCounterparty = useCallback(
    (value) => {
      changeCounterparty(value.target.value);

      if (value.target.value) {
        changeOgrnip(undefined);
        changeCounterpartyLastName(undefined);
        changeCounterpartyFirstName(undefined);
        changeCounterpartyMiddleName(undefined);
      } else {
        changeKpp(undefined);
      }
    },
    [
      changeCounterparty,
      changeCounterpartyFirstName,
      changeCounterpartyLastName,
      changeCounterpartyMiddleName,
      changeKpp,
      changeOgrnip,
    ],
  );

  const onChangeCompanyCounterparty = useCallback(
    (value) => {
      changeCompanyCounterparty(value.target.value);

      if (value.target.value) {
        changeOrgLocal(undefined);
        changeIsPayAgent(false);
      }
    },
    [changeCompanyCounterparty, changeIsPayAgent, changeOrgLocal],
  );

  const renderTypeName = useCallback(({ code, name }) => `${code} - ${name}`, []);

  const innMask = useMemo(() => createMask('ONLY_NUMBERS'), []);
  const kppMask = useMemo(() => createMask('KPP'), []);

  return (
    <>
      <div className={styles.radiosRow}>
        <RadioField
          name="counterparty"
          label="Вид контрагента"
          disabled={!isNew}
          options={COUNTERPARTY_OPTIONS}
          onChange={onChangeCounterparty}
        />
        <RadioField
          name="resident"
          label="Признак налогового резидента"
          disabled={!isEditMode}
          options={RESIDENT_OPTIONS}
          onChange={onChangeResident}
        />
        <RadioField
          name="companyCounterparty"
          label="Компания - владелец сервиса РМ"
          disabled={!isNew}
          options={COMPANY_COUNTERPARTY_OPTIONS}
          onChange={onChangeCompanyCounterparty}
        />
        {companyCounterparty === false && (
          <>
            <SelectField
              name="counterpartyTypeCode"
              label="Тип контрагента"
              disabled={!isNew}
              size="small"
              groupStyle={!isEditMode}
              items={counterpartyTypes}
              renderName={renderTypeName}
              renderValue={(item) => item.code}
              className={styles.select}
            />
            <SelectField
              name="orgLocal"
              label="Принадлежность к справочнику организации"
              disabled={!isNew}
              size="small"
              groupStyle={!isEditMode}
              items={organizations}
              allowClear
              className={styles.select}
            />
          </>
        )}
      </div>

      <div className={styles.row}>
        <TextField
          name="code"
          label="Код контрагента"
          disabled={!isNew}
          size="small"
          groupStyle={!isEditMode}
          maxLength={50}
          required
        />
        <TextField
          name="inn"
          label="ИНН"
          disabled={!isEditMode}
          size="small"
          groupStyle={!isEditMode}
          maxLength={counterparty ? 10 : 12}
          mask={innMask}
          unmask
          required={resident}
        />
        <TextField
          name="taxpayerCode"
          label="Код налогоплательщика в стране регистрации"
          disabled={!isEditMode}
          size="small"
          groupStyle={!isEditMode}
          maxLength={80}
        />
      </div>

      <div className={styles.row}>
        <TextField
          name="registrationNumber"
          label="Регистрационный номер"
          disabled={!isEditMode}
          size="small"
          groupStyle={!isEditMode}
          maxLength={resident ? 13 : 100}
          required={!resident}
          mask={resident ? createMask('ONLY_NUMBERS') : undefined}
        />

        {counterparty && resident && (
          <TextField
            name="kpp"
            label="КПП"
            disabled={!isEditMode}
            size="small"
            groupStyle={!isEditMode}
            maxLength={9}
            mask={kppMask}
            unmask
            required={resident}
          />
        )}
        <TextField
          name="thesOksmCode"
          label="Код ОКСМ"
          disabled={!isEditMode || resident}
          size="small"
          groupStyle={!isEditMode}
          required={!resident}
          maxLength={3}
          mask={createMask('ONLY_NUMBERS')}
          unmask
        />
        {!counterparty && (
          <TextField
            name="ogrnip"
            label="ОГРНИП"
            disabled={!isEditMode}
            size="small"
            groupStyle={!isEditMode}
            maxLength={15}
            mask={createMask('ONLY_NUMBERS')}
          />
        )}
      </div>

      <div className={styles.row}>
        <TextAreaField
          name="name"
          label="Краткое наименование"
          disabled={!isEditMode}
          groupStyle={!isEditMode}
          size="small"
          maxLength={1000}
          required
          autoSize
          rows={1}
        />
        <TextAreaField
          name="fullName"
          label="Полное наименование"
          disabled={!isEditMode}
          groupStyle={!isEditMode}
          maxLength={1000}
          size="small"
          autoSize
          rows={1}
          required
        />
        <TextAreaField
          name="latinName"
          label="Наименование на латинице"
          disabled={!isEditMode}
          groupStyle={!isEditMode}
          maxLength={1000}
          size="small"
          autoSize
          rows={1}
        />
      </div>

      <div className={styles.row}>
        <TextAreaField
          name="legalAddress"
          groupStyle={!isEditMode}
          label="Юридический адрес (адрес регистрации контрагента - физического лица)"
          size="small"
          disabled={!isEditMode}
          maxLength={1000}
          autoSize
          rows={1}
        />
      </div>

      {companyCounterparty === false && (
        <CheckboxField name="isPayAgent" label="Признак платежного агента" groupStyle disabled={!isEditMode} />
      )}

      {!counterparty && (
        <GroupPanel
          label="Наименование ИП или физического лица"
          className={!isEditMode ? styles.groupPanel : undefined}
        >
          <div className={styles.row}>
            <TextField
              name="counterpartyLastName"
              groupStyle={!isEditMode}
              label="Фамилия"
              size="small"
              disabled={!isEditMode}
              maxLength={60}
              required={!counterparty}
            />
            <TextField
              name="counterpartyFirstName"
              groupStyle={!isEditMode}
              label="Имя"
              size="small"
              disabled={!isEditMode}
              maxLength={60}
              required={!counterparty}
            />
            <TextField
              name="counterpartyMiddleName"
              groupStyle={!isEditMode}
              label="Отчество"
              size="small"
              disabled={!isEditMode}
              maxLength={60}
            />
          </div>
        </GroupPanel>
      )}

      <div className={cm(styles.row, styles.rowDate)}>
        <DateField
          name="startDate"
          label="Дата начала действия записи"
          disabled={!isEditMode}
          size="small"
          groupStyle
          required
        />
        <DateField
          name="endDate"
          label="Дата окончания действия записи"
          disabled={!isEditMode}
          size="small"
          groupStyle
        />
      </div>
    </>
  );
});
